
import React, { useState, useRef, useEffect } from 'react';
import { CheckerResult, Language, TRANSLATIONS, Theme } from '../types';
import { Upload, ScanEye, CheckCircle, Layers, Layout, Type, Image, AppWindow, ListOrdered, Monitor, Globe, RotateCcw, AlertTriangle, ChevronDown, MessageSquareText, Languages, Component, Maximize, MousePointerClick, ArrowLeft, FileText, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { Loader } from './Loader';

interface CheckerModeProps {
  language: Language;
  theme: Theme;
  onShowCaptureMaintenance: () => void;
}

type OverlayRect = {
  topPct: number;
  leftPct: number;
  widthPct: number;
  heightPct: number;
};

type OverlayContext = {
  yxMaxX: number;
  yxMaxY: number;
  xyMaxX: number;
  xyMaxY: number;
  fallbackWidth: number;
  fallbackHeight: number;
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const inferAxisBase = (maxValue: number, fallback: number) => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return fallback > 0 ? fallback : 1000;
  if (maxValue <= 1.01) return 1;
  if (maxValue <= 100.5) return 100;
  if (maxValue <= 1000.5) return 1000;
  return fallback > 0 ? fallback : maxValue;
};

const toPercent = (value: number, axisBase: number) => {
  if (!Number.isFinite(value) || !Number.isFinite(axisBase) || axisBase <= 0) return 0;
  if (axisBase === 1) return value * 100;
  if (axisBase === 100) return value;
  if (axisBase === 1000) return value / 10;
  return (value / axisBase) * 100;
};

const scoreOverlayRect = (rect: OverlayRect | null) => {
  if (!rect) return -Infinity;
  const { topPct, leftPct, widthPct, heightPct } = rect;
  if (!Number.isFinite(topPct) || !Number.isFinite(leftPct) || !Number.isFinite(widthPct) || !Number.isFinite(heightPct)) {
    return -Infinity;
  }
  if (widthPct <= 0 || heightPct <= 0) return -Infinity;
  let score = 0;
  if (widthPct >= 0.6 && heightPct >= 0.6) score += 2;
  if (widthPct <= 95 && heightPct <= 95) score += 1;
  if (topPct >= 0 && leftPct >= 0 && topPct + heightPct <= 100.5 && leftPct + widthPct <= 100.5) score += 2;
  const area = widthPct * heightPct;
  if (area >= 0.5 && area <= 90 * 90) score += 2;
  if (topPct < 0 || leftPct < 0 || topPct > 100 || leftPct > 100) score -= 3;
  return score;
};

const rectFromBox = (
  box: number[],
  order: 'yx' | 'xy',
  context: OverlayContext,
): OverlayRect | null => {
  if (!Array.isArray(box) || box.length < 4) return null;
  const values = box.map((value) => Number(value));
  if (values.some((value) => !Number.isFinite(value))) return null;

  const [a, b, c, d] = values;
  const [y1, x1, y2, x2] = order === 'yx' ? [a, b, c, d] : [b, a, d, c];

  const xmin = Math.min(x1, x2);
  const xmax = Math.max(x1, x2);
  const ymin = Math.min(y1, y2);
  const ymax = Math.max(y1, y2);

  const axisBaseX = inferAxisBase(order === 'yx' ? context.yxMaxX : context.xyMaxX, context.fallbackWidth);
  const axisBaseY = inferAxisBase(order === 'yx' ? context.yxMaxY : context.xyMaxY, context.fallbackHeight);

  const leftPct = toPercent(xmin, axisBaseX);
  const topPct = toPercent(ymin, axisBaseY);
  const rightPct = toPercent(xmax, axisBaseX);
  const bottomPct = toPercent(ymax, axisBaseY);

  return {
    topPct: clampPercent(topPct),
    leftPct: clampPercent(leftPct),
    widthPct: Math.max(0, clampPercent(rightPct) - clampPercent(leftPct)),
    heightPct: Math.max(0, clampPercent(bottomPct) - clampPercent(topPct)),
  };
};

const resolveOverlayRect = (box: number[] | undefined, context: OverlayContext): OverlayRect | null => {
  if (!box || box.length < 4) return null;
  const yxRect = rectFromBox(box, 'yx', context);
  const xyRect = rectFromBox(box, 'xy', context);
  const yxScore = scoreOverlayRect(yxRect) + 0.25; // slight preference for yx order
  const xyScore = scoreOverlayRect(xyRect);
  return yxScore >= xyScore ? yxRect : xyRect;
};

const GlobalAccordion: React.FC<{
  title: string;
  content: string[];
  icon: React.ReactNode;
  theme: Theme;
}> = ({ title, content, icon, theme }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!content || content.length === 0) return null;

  const isDark = theme === 'dark';
  const containerClass = isDark ? 'bg-black border-slate-800' : 'bg-white/80 border-slate-200/60 shadow-sm hover:border-black';
  const textClass = isDark ? 'text-white' : 'text-slate-900';
  const iconBg = isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200 text-slate-700';
  const listText = isDark ? 'text-slate-300' : 'text-slate-600';
  const hoverClass = isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-50';

  return (
    <div className={`${containerClass} border rounded-xl transition-all duration-300`}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-4 p-5 cursor-pointer select-none group active:scale-[0.99] transition-transform text-left rounded-xl ${hoverClass}`}
        aria-expanded={isOpen}
      >
        <div className="shrink-0">
            <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shadow-sm group-hover:border-blue-400 transition-colors ${iconBg}`}>
              {icon}
            </div>
        </div>
        
        <div className="flex-grow flex items-center justify-between">
            <h4 className={`font-bold text-base tracking-wide group-hover:text-blue-500 transition-colors uppercase ${textClass}`}>{title}</h4>
            <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
        </div>
      </button>

      {isOpen && (
        <div className="px-6 pb-6 pt-0 pl-6 md:pl-[4.5rem] animate-fade-in">
            <div className={`border-t pt-3 ${isDark ? 'border-slate-800/50' : 'border-slate-100'}`}>
              <ul className={`list-disc pl-5 space-y-2 font-mono text-sm leading-relaxed ${listText}`}>
                {content.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
        </div>
      )}
    </div>
  );
};

// Category block inside component accordion (always expanded)
const CategoryBlock: React.FC<{
  title: string;
  content: string[];
  theme: Theme;
}> = ({ title, content, theme }) => {
  if (!content || content.length === 0) return null;

  const isDark = theme === 'dark';
  const titleClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const textClass = isDark ? 'text-slate-300' : 'text-slate-700';
  const itemBorderClass = isDark ? 'border-slate-700/50' : 'border-slate-200';
  const blockBorderClass = isDark ? 'border-slate-700/30' : 'border-slate-200';

  return (
    <div className={`mb-3 border-l-2 ml-2 pl-4 pr-2 py-2 ${blockBorderClass}`}>
      <h6 className={`font-bold text-xs uppercase tracking-wider mb-2 ${titleClass}`}>{title}</h6>
      <ul className={`list-disc pl-4 space-y-2 text-xs font-mono leading-relaxed ${textClass}`}>
        {content.map((item, idx) => (
          <li key={idx} className={`pl-2 border-l ${itemBorderClass}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

// Accordion for the SECTION itself
const SectionAccordion: React.FC<{
  title: string;
  children: React.ReactNode;
  isSelected?: boolean;
  theme: Theme;
}> = ({ title, children, isSelected, theme }) => {
  const [isOpen, setIsOpen] = useState(true);
  const isDark = theme === 'dark';

  let containerClass = '';
  let headerClass = '';
  let titleClass = '';
  let hoverClass = '';

  if (isDark) {
      containerClass = isSelected ? 'bg-blue-900/10 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.2)]' : 'bg-slate-900/30 border-slate-800';
      headerClass = isSelected ? 'bg-blue-900/20 border-blue-500/30' : 'bg-slate-900 border-slate-800';
      titleClass = isSelected ? 'text-blue-200' : 'text-white';
      hoverClass = 'hover:bg-slate-800';
  } else {
      containerClass = isSelected ? 'bg-blue-50/50 border-blue-300 shadow-md shadow-blue-200/50' : 'bg-white/80 border-slate-200 shadow-sm hover:border-black';
      headerClass = isSelected ? 'bg-blue-100/30 border-blue-200' : 'bg-white/50 border-slate-200';
      titleClass = isSelected ? 'text-blue-700' : 'text-slate-900';
      hoverClass = 'hover:bg-slate-50';
  }

  return (
    <div className={`border rounded-xl overflow-hidden mb-4 transition-all duration-300 ${containerClass}`}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full border-b p-4 cursor-pointer flex justify-between items-center group transition-colors active:scale-[0.99] ${headerClass} ${hoverClass}`}
        aria-expanded={isOpen}
      >
          <h4 className={`font-bold text-lg group-hover:text-blue-500 transition-colors text-left ${titleClass}`}>{title}</h4>
          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      
      {isOpen && (
        <div className="p-2 animate-fade-in">
           {children}
        </div>
      )}
    </div>
  );
}

const ComponentAccordion: React.FC<{
  title: string;
  children: React.ReactNode;
  theme: Theme;
}> = ({ title, children, theme }) => {
  const [isOpen, setIsOpen] = useState(true);
  const isDark = theme === 'dark';
  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className={`w-full flex items-center justify-between px-1 py-2 text-left transition-colors ${
          isDark ? 'text-slate-200 hover:text-white' : 'text-slate-700 hover:text-slate-900'
        }`}
      >
        <h5 className="text-sm font-semibold">{title}</h5>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="pt-1 pb-1 animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
};

const DetectedScreenAccordion: React.FC<{
  t: any;
  screenContext: CheckerResult['screenContext'];
  imageResolution: string | null;
  renderContextValue: (val: string | number | undefined | null) => React.ReactNode;
  theme: Theme;
}> = ({ t, screenContext, imageResolution, renderContextValue, theme }) => {
  const [isOpen, setIsOpen] = useState(true);
  const isDark = theme === 'dark';

  const containerClass = isDark ? 'bg-black border-slate-800' : 'bg-white/90 border-slate-200 shadow-sm hover:border-black';
  const itemBg = isDark ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200';
  const hoverClass = isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-50';

  return (
    <div className={`${containerClass} border rounded-xl overflow-hidden mb-6`}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-6 cursor-pointer select-none group border-b flex justify-between items-center active:bg-opacity-80 transition-colors text-left ${isDark ? 'border-slate-800/50' : 'border-slate-100'} ${hoverClass}`}
        aria-expanded={isOpen}
      >
        <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wider flex items-center gap-2 group-hover:text-blue-400 transition-colors">
            <Monitor className="w-4 h-4" /> {t.detectedScreen}
        </h3>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="p-6 animate-fade-in">
          <div className="space-y-4">
              {screenContext?.description && (
                  <div className={`text-sm italic border-l-2 pl-3 ${isDark ? 'text-slate-300 border-slate-700' : 'text-slate-600 border-slate-300'}`}>"{screenContext.description}"</div>
              )}
              
              {/* Context Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                      { icon: Monitor, label: t.device, val: screenContext?.device },
                      { icon: Globe, label: t.sector, val: screenContext?.sector },
                      { icon: Layout, label: t.screenType, val: screenContext?.screenType },
                      { icon: Maximize, label: t.screenSize, val: imageResolution },
                      { icon: Languages, label: t.uiLanguage, val: screenContext?.language },
                      { icon: Component, label: t.componentCount, val: screenContext?.componentCount },
                  ].map((item, i) => (
                    <div key={i} className={`${itemBg} p-3 rounded-xl border`}>
                        <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase font-bold mb-1">
                            <item.icon className="w-3 h-3" aria-hidden="true" /> {item.label}
                        </div>
                        <div className="font-medium text-sm truncate">{renderContextValue(item.val)}</div>
                    </div>
                  ))}
              </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const CheckerMode: React.FC<CheckerModeProps> = ({ language, theme, onShowCaptureMaintenance }) => {
  const ANALYSIS_QUERY_KEY = 'analysis';
  const ANALYSIS_STORAGE_PREFIX = 'aaaquest_checker_analysis_';
  // View State
  const [viewState, setViewState] = useState<'UPLOAD' | 'ANALYZING' | 'RESULT'>('UPLOAD');
  const [image, setImage] = useState<string | null>(null); // base64
  const [imageResolution, setImageResolution] = useState<string | null>(null);
  const [analysisImageSize, setAnalysisImageSize] = useState<{ width: number; height: number } | null>(null);
  const [result, setResult] = useState<CheckerResult | null>(null);
  const [userContext, setUserContext] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [isFullWidthOverride, setIsFullWidthOverride] = useState<boolean | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Interaction State
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number | null>(null);
  const [hoveredSectionIndex, setHoveredSectionIndex] = useState<number | null>(null);

  // Reset scroll on viewState change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [viewState]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get(ANALYSIS_QUERY_KEY);
    if (!id) return;
    const raw = sessionStorage.getItem(`${ANALYSIS_STORAGE_PREFIX}${id}`);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as {
        image?: string | null;
        imageResolution?: string | null;
        analysisImageSize?: { width?: number; height?: number } | null;
        result?: CheckerResult | null;
        userContext?: string;
        selectedSectionIndex?: number | null;
        language?: Language;
      };
      if (parsed?.image && parsed?.result) {
        setAnalysisId(id);
        setImage(parsed.image);
        setImageResolution(parsed.imageResolution ?? null);
        const restoredWidth = Number(parsed.analysisImageSize?.width);
        const restoredHeight = Number(parsed.analysisImageSize?.height);
        if (Number.isFinite(restoredWidth) && restoredWidth > 0 && Number.isFinite(restoredHeight) && restoredHeight > 0) {
          setAnalysisImageSize({ width: restoredWidth, height: restoredHeight });
        } else {
          setAnalysisImageSize(null);
        }
        setResult(parsed.result);
        setUserContext(parsed.userContext ?? '');
        setSelectedSectionIndex(parsed.selectedSectionIndex ?? null);
        setHoveredSectionIndex(null);
        setViewState('RESULT');
      }
    } catch {
      // ignore parse failures
    }
  }, []);

  const t = TRANSLATIONS[language];
  const isDark = theme === 'dark';

  // Theme Constants
  const textMain = isDark ? 'text-white' : 'text-slate-900';
  const textSub = isDark ? 'text-slate-400' : 'text-slate-500';
  const glassPanelClass = isDark ? 'glass-panel' : 'bg-white/60 backdrop-blur-xl border border-slate-200/60 shadow-xl';
  const inputBg = isDark ? 'bg-slate-900/50 border-slate-700 text-slate-200 placeholder-slate-600' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400';
  
  const processFile = (file: File) => {
    setDragError(null);
    // Check for valid image types
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
        setDragError(t.analysisErrorUnsupportedFormat);
        return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = document.createElement('img');
      img.onload = () => {
        setImageResolution(`${img.naturalWidth}x${img.naturalHeight}`);
        const MAX_SIZE = 1024;
        let width = img.naturalWidth;
        let height = img.naturalHeight;
        if (width > MAX_SIZE || height > MAX_SIZE) {
          if (width > height) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          } else {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        const base64Data = compressedDataUrl.split(',')[1];
        setAnalysisImageSize({ width, height });
        setImage(base64Data);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
        processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    if (dragError) setDragError(null);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragError(null);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleAnalyze = async () => {
    if (!image || isSubmitting) return;
    setAnalysisError(null);
    onShowCaptureMaintenance();
  };

  const handleReset = () => {
    if (analysisId) {
      sessionStorage.removeItem(`${ANALYSIS_STORAGE_PREFIX}${analysisId}`);
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has(ANALYSIS_QUERY_KEY)) {
      params.delete(ANALYSIS_QUERY_KEY);
      const nextQuery = params.toString();
      history.replaceState(null, '', `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`);
    }
    setImage(null);
    setImageResolution(null);
    setAnalysisImageSize(null);
    setResult(null);
    setUserContext('');
    setViewState('UPLOAD');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setSelectedSectionIndex(null);
    setHoveredSectionIndex(null);
    setAnalysisError(null);
    setAnalysisId(null);
    setIsFullWidthOverride(null);
  };

  useEffect(() => {
    const handleCheckerReset = () => {
      handleReset();
    };

    window.addEventListener('aaaquest:checker-reset', handleCheckerReset as EventListener);
    return () => {
      window.removeEventListener('aaaquest:checker-reset', handleCheckerReset as EventListener);
    };
  }, [analysisId, image, result, userContext]);

  const handleZoneClick = (index: number) => {
    const nextIndex = selectedSectionIndex === index ? null : index;
    setSelectedSectionIndex(nextIndex);
    if (analysisId && image && result) {
      try {
        const snapshot = {
          image,
          imageResolution,
          analysisImageSize,
          result,
          userContext,
          selectedSectionIndex: nextIndex,
          language,
        };
        sessionStorage.setItem(
          `${ANALYSIS_STORAGE_PREFIX}${analysisId}`,
          JSON.stringify(snapshot),
        );
      } catch {
        // ignore storage errors
      }
    }
  };

  const handleExportPDF = async () => {
    if (!result || !image) return;

    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    let yPos = 15;

    const colorSlate900 = '#0f172a';
    const colorSlate500 = '#64748b';
    const colorBlue600 = '#2563eb';

    const drawHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(colorSlate900);
        doc.text(t.checkerPdfTitle, margin, 17);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(colorSlate500);
        doc.text(new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US'), pageWidth - margin, 17, { align: 'right' });
    };

    const drawFooter = () => {
        const text = t.checkerPdfFooter;
        const linkUrl = "https://www.linkedin.com/in/victorsaizalfageme/";
        doc.setFontSize(8);
        doc.setTextColor(colorSlate500);
        doc.setFont("helvetica", "normal");
        const textWidth = doc.getTextWidth(text);
        const x = (pageWidth - textWidth) / 2;
        const y = pageHeight - 8;
        doc.text(text, x, y);
        doc.link(x, y - 3, textWidth, 4, { url: linkUrl });
    };

    const checkPageBreak = (heightNeeded: number) => {
        if (yPos + heightNeeded > pageHeight - margin - 10) { 
            drawFooter();
            doc.addPage();
            drawHeader();
            yPos = 25;
            return true;
        }
        return false;
    };

    const addText = (text: string, x: number, y: number, fontSize: number = 9, isBold: boolean = false, color: string = '#000000', maxWidth?: number) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      doc.setTextColor(color);
      if (maxWidth) {
          doc.text(doc.splitTextToSize(text, maxWidth), x, y);
      } else {
          doc.text(text, x, y);
      }
    };

    const getCategoryHeight = (cats: {title: string, items: string[]}[], w: number) => {
        let h = 0;
        cats.forEach(c => {
            if (c.items && c.items.length > 0) {
                h += 3.5;
                c.items.forEach((item: string) => {
                    const lines = doc.splitTextToSize(`- ${item}`, w);
                    h += (lines.length * 3.2);
                });
                h += 2;
            }
        });
        return h;
    };

    const renderCategoryColumn = (cats: {title: string, items: string[]}[], x: number, startY: number, w: number) => {
        let curY = startY;
        cats.forEach(c => {
           if (c.items && c.items.length > 0) {
               doc.setFontSize(8);
               doc.setFont("helvetica", "bold");
               doc.setTextColor(colorSlate500);
               doc.text(c.title.toUpperCase(), x, curY);
               curY += 3.5;
               c.items.forEach((item: string) => {
                   const lines = doc.splitTextToSize(`- ${item}`, w);
                   doc.setFontSize(8);
                   doc.setFont("helvetica", "normal");
                   doc.setTextColor('#475569');
                   doc.text(lines, x, curY);
                   curY += (lines.length * 3.2);
               });
               curY += 2;
           }
        });
        return curY;
    };

    drawHeader();
    yPos = 25;

    const ctx = result.screenContext;
    const splitY = yPos;
    const imgProps = doc.getImageProperties(`data:image/jpeg;base64,${image}`);
    const maxImgWidth = 60;
    const maxImgHeight = 50;
    let imgW = maxImgWidth;
    let imgH = (imgProps.height * imgW) / imgProps.width;
    
    if (imgH > maxImgHeight) {
        imgH = maxImgHeight;
        imgW = (imgProps.width * imgH) / imgProps.height;
    }
    
    doc.addImage(`data:image/jpeg;base64,${image}`, 'JPEG', margin, yPos, imgW, imgH);
    
    const textX = margin + maxImgWidth + 5;
    let textY = splitY;
    
    addText(t.checkerPdfScreenSummary, textX, textY, 11, true, colorSlate900);
    textY += 5;
    
    if (ctx) {
        const details = [
            `${t.device}: ${ctx.device || '-'}`,
            `${t.screenType}: ${ctx.screenType || '-'}`,
            `${t.sector}: ${ctx.sector || '-'}`,
            `${t.uiLanguage}: ${ctx.language || '-'}`,
            `${t.componentCount}: ${ctx.componentCount || '-'}`,
            `${language === 'es' ? 'Resolución' : 'Resolution'}: ${imageResolution || '-'}`
        ];
        
        details.forEach(det => {
            addText(det, textX, textY, 9, false, '#334155');
            textY += 4;
        });
        
        if (ctx.description) {
            textY += 1;
            addText(`"${ctx.description}"`, textX, textY, 8, false, '#64748b', pageWidth - textX - margin);
        }
    }
    yPos = Math.max(yPos + imgH, textY) + 8;

    checkPageBreak(30);
    
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, yPos - 3, pageWidth - margin, yPos - 3);

    addText(t.checkerPdfGlobalAccessibility, margin, yPos, 11, true, colorSlate900);
    yPos += 5;

    const globals = [
        { title: t.checkerPdfViewTitle, items: result.globalAnnotations?.viewTitle },
        { title: t.checkerPdfStructure, items: result.globalAnnotations?.structure },
        { title: t.checkerPdfHeading, items: result.globalAnnotations?.heading },
        { title: t.checkerPdfFocusOrder, items: result.globalAnnotations?.focusOrder },
    ];

    let col = 0;
    let maxRowHeight = 0;
    let rowStartY = yPos;
    const colWidth = (pageWidth - (margin * 2) - 5) / 2;

    globals.forEach((g) => {
        if (!g.items || g.items.length === 0) return;
        
        const currentX = margin + (col * (colWidth + 5));
        let currentY = rowStartY;
        let neededH = 4;
        g.items.forEach(item => {
             const lines = doc.splitTextToSize(`• ${item}`, colWidth);
             neededH += (lines.length * 3.5);
        });

        if (currentY + neededH > pageHeight - margin - 10) {
             drawFooter();
             doc.addPage();
             drawHeader();
             rowStartY = 25;
             currentY = 25;
             maxRowHeight = 0;
        }

        addText(g.title, currentX, currentY, 9, true, colorBlue600);
        currentY += 4;
        
        g.items.forEach(item => {
            const lines = doc.splitTextToSize(`• ${item}`, colWidth);
            doc.setFontSize(8);
            doc.setTextColor('#334155');
            doc.setFont("helvetica", "normal");
            doc.text(lines, currentX, currentY);
            currentY += (lines.length * 3.5);
        });

        if (currentY - rowStartY > maxRowHeight) maxRowHeight = currentY - rowStartY;

        col++;
        if (col > 1) {
            col = 0;
            rowStartY += maxRowHeight + 4;
            maxRowHeight = 0;
        }
    });
    yPos = rowStartY + maxRowHeight + 4;

    if (result.complexSections) {
        checkPageBreak(15);
        
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, yPos - 3, pageWidth - margin, yPos - 3);

        addText(t.checkerPdfSectionsDetected, margin, yPos, 11, true, colorSlate900);
        yPos += 5;

        result.complexSections.forEach((section, idx) => {
             const leftCats = [
                 { title: t.checkerPdfGeneral, items: section.categories.generalSpecific },
                 { title: t.checkerPdfBehavior, items: section.categories.behavior },
             ];
             const rightCats = [
                 { title: t.checkerPdfAlt, items: section.categories.alt },
                 { title: t.checkerPdfGrouped, items: section.categories.grouped },
             ];

             const leftH = getCategoryHeight(leftCats, colWidth);
             const rightH = getCategoryHeight(rightCats, colWidth);
             const sectionContentHeight = Math.max(leftH, rightH);
             const headerHeight = 7;
             const totalSectionHeight = headerHeight + sectionContentHeight + 5;

             checkPageBreak(totalSectionHeight);

             doc.setFillColor(248, 250, 252);
             doc.roundedRect(margin, yPos - 3, pageWidth - (margin*2), 7, 1, 1, 'F');
             doc.setFontSize(9);
             doc.setFont("helvetica", "bold");
             doc.setTextColor(30, 41, 59);
             doc.text(`${idx + 1}. ${section.regionTitle}`, margin + 2, yPos + 1.5);
             yPos += 6;

             const startSecY = yPos;
             renderCategoryColumn(leftCats, margin, startSecY, colWidth);
             renderCategoryColumn(rightCats, margin + colWidth + 5, startSecY, colWidth);
             
             yPos += sectionContentHeight + 5;
        });
    }

    drawFooter();
    doc.save(t.checkerReportFileName);
  };

  const renderContextValue = (val: string | number | undefined | null) => {
    if (val === undefined || val === null || val === '') {
      return <span className={`italic ${isDark ? 'text-red-400/80' : 'text-red-500'}`}>{t.analysisIncorrect}</span>;
    }
    return <span className={`font-medium text-sm ${textMain}`}>{val}</span>;
  };

  // --- VIEW: UPLOAD ---
  if (viewState === 'UPLOAD') {
    return (
      <div className="max-w-6xl mx-auto px-4 pb-4 pt-0 md:px-8 md:pb-8 md:pt-0 animate-fade-in">
        <div className="text-left md:text-center mb-8">
          <h2 className={`text-4xl font-bold mb-2 flex justify-start md:justify-center items-center gap-3 ${textMain}`}>
            <ScanEye className={`w-10 h-10 ${isDark ? 'text-green-500' : 'text-emerald-600'}`} aria-hidden="true" /> 
            <span className={`text-transparent bg-clip-text ${isDark ? 'bg-gradient-to-r from-green-400 to-emerald-600' : 'bg-gradient-to-r from-emerald-600 to-green-800'}`}>{t.checkerTitle}</span>
          </h2>
          <p className={`text-base md:text-lg font-light leading-relaxed max-w-2xl md:mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {t.checkerSubtitle}
          </p>
        </div>

        <div className={`${glassPanelClass} rounded-xl p-6`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* 1. IMAGE PREVIEW (Mobile: Order 3, Desktop: Left Column) */}
            <div className={`order-3 lg:order-1 ${isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-slate-50 border-slate-200 shadow-inner'} rounded-xl h-64 lg:h-full min-h-[200px] flex items-center justify-center border overflow-hidden relative group`}>
              {image ? (
                <>
                  <img src={`data:image/png;base64,${image}`} alt="Preview" className="max-h-full max-w-full object-contain p-2" />
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white font-medium mb-2">{t.preview}</p>
                  </div>
                </>
              ) : (
                <div className={`flex flex-col items-center ${textSub}`}>
                   <div className={`w-20 h-20 rounded-xl flex items-center justify-center mb-4 ${isDark ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
                     <Image className="w-10 h-10 opacity-30" aria-hidden="true" />
                   </div>
                   <p className="text-sm">{t.imagePlaceholder}</p>
                </div>
              )}
            </div>

            {/* Right Column Wrapper */}
            <div className="contents lg:flex lg:flex-col lg:gap-6 lg:order-2">
                {/* 2. TITLE (Mobile: Order 1) */}
                <div className="order-1">
                   <div>
                      <h3 className={`text-2xl font-bold mb-2 ${textMain}`}>{t.uploadTitle}</h3>
                      <p className={`${textSub} leading-relaxed text-sm`}>{t.uploadDesc}</p>
                   </div>
                </div>

                {/* 3. INPUTS (Mobile: Order 2) */}
                <div className="order-2 space-y-6">
                   <>
                     <div 
                        className={`border-2 rounded-xl p-8 text-center transition-all group ${
                          dragError
                              ? (isDark ? 'border-red-500 bg-red-900/10' : 'border-red-500 bg-red-50')
                              : isDragging 
                                  ? (isDark ? 'border-emerald-400 bg-emerald-900/20 scale-[1.02]' : 'border-emerald-500 bg-emerald-50 scale-[1.02]')
                                  : image 
                                      ? 'border-green-500/50 bg-green-500/5 border-dashed' 
                                      : isDark ? 'border-slate-700 hover:border-white hover:bg-slate-800/50 border-dashed' : 'border-slate-300 hover:border-black hover:bg-emerald-50/50 border-dashed'
                        } cursor-pointer active:scale-[0.99]`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            setDragError(null);
                            fileInputRef.current?.click();
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              fileInputRef.current?.click();
                          }
                        }}
                        aria-label={image ? `${t.imageLoaded}. ${t.clickToChange}` : t.uploadFile}
                      >
                        <input 
                          type="file" 
                          ref={fileInputRef} 
                          className="hidden" 
                          accept="image/*"
                          onChange={handleFileUpload}
                        />
                        
                        {dragError ? (
                          <div className={`${isDark ? 'text-red-400' : 'text-red-500'} flex flex-col items-center pointer-events-none`}>
                            <AlertTriangle className="w-10 h-10 mb-2" aria-hidden="true" />
                            <span className="font-bold">{dragError}</span>
                            <span className="text-xs opacity-60 mt-1">{t.clickToChange}</span>
                          </div>
                        ) : image ? (
                          <div className="text-green-500 flex flex-col items-center pointer-events-none">
                            <CheckCircle className="w-10 h-10 mb-2" aria-hidden="true" />
                            <span className="font-bold">{t.imageLoaded}</span>
                            <span className="text-xs opacity-60">{t.clickToChange}</span>
                          </div>
                        ) : (
                          <div className={`${textSub} flex flex-col items-center pointer-events-none ${isDark ? 'group-hover:text-white' : 'group-hover:text-emerald-700'} transition-colors`}>
                            <Upload className={`w-10 h-10 mb-3 opacity-50 transition-transform ${isDragging ? 'scale-110 text-emerald-500' : 'group-hover:scale-105'}`} aria-hidden="true" />
                            <span className="font-medium">{isDragging ? t.checkerDropFile : t.uploadFile}</span>
                            <span className="text-xs mt-1 opacity-60">{t.formats}</span>
                          </div>
                        )}
                     </div>

                     <div className="space-y-2">
                       <label htmlFor="userContext" className={`block text-sm font-bold ${textSub}`}>
                         {t.contextOptional}
                       </label>
                       <textarea
                          id="userContext"
                          value={userContext}
                          onChange={(e) => setUserContext(e.target.value)}
                          placeholder={t.contextPlaceholder}
                          className={`w-full rounded-xl p-3 text-sm min-h-[80px] resize-none border transition-all ${inputBg}`}
                       />
                     </div>
                   </>
                </div>

                {/* 4. CTA (Mobile: Order 4) */}
                <div className="order-4 space-y-2 mt-4">
                       <button
                          onClick={handleAnalyze}
                          disabled={!image || isSubmitting}
                          className="w-full py-4 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-normal text-lg transition-all flex items-center justify-center gap-3 border border-transparent active:scale-95 bg-[#038759] hover:bg-[#026e49]"
                        >
                            <ScanEye className="w-6 h-6" aria-hidden="true" /> {t.analyzeBtn}
                        </button>
                       {analysisError && (
                         <div
                           role="alert"
                           className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
                         >
                           {analysisError}
                         </div>
                       )}
                </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- VIEW: ANALYZING ---
  if (viewState === 'ANALYZING') {
    return (
       <div className="flex items-center justify-center min-h-[80vh] w-full animate-fade-in" role="status" aria-live="polite">
          <Loader text={t.analyzing} theme={theme} />
       </div>
    );
  }

  // --- VIEW: RESULT ---
  if (viewState === 'RESULT' && result) {
    const isComplete = result.isValidUI;
    const device = result.screenContext?.device?.toLowerCase() || '';
    const isDesktopScreenshot = device.includes('desktop') || device.includes('monitor') || device.includes('laptop') || device.includes('web') || device.includes('mac') || device.includes('windows');
    const isFullWidthLayout = isFullWidthOverride ?? isDesktopScreenshot;
    const overlayBoxes = result.complexSections
      .map((section) => section.box_2d)
      .filter((box): box is number[] => Array.isArray(box) && box.length >= 4);
    const overlayContext: OverlayContext = {
      yxMaxX: Math.max(0, ...overlayBoxes.flatMap((box) => [Number(box[1]) || 0, Number(box[3]) || 0])),
      yxMaxY: Math.max(0, ...overlayBoxes.flatMap((box) => [Number(box[0]) || 0, Number(box[2]) || 0])),
      xyMaxX: Math.max(0, ...overlayBoxes.flatMap((box) => [Number(box[0]) || 0, Number(box[2]) || 0])),
      xyMaxY: Math.max(0, ...overlayBoxes.flatMap((box) => [Number(box[1]) || 0, Number(box[3]) || 0])),
      fallbackWidth: analysisImageSize?.width ?? 0,
      fallbackHeight: analysisImageSize?.height ?? 0,
    };
    const toggleLayout = () => {
      setIsFullWidthOverride((prev) => {
        if (prev === null) return !isDesktopScreenshot;
        return !prev;
      });
    };

    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8 animate-fade-in pb-12">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-6 lg:gap-0 mb-8">
           <div className="w-full lg:w-auto">
              <h2 className={`text-3xl font-bold mb-2 ${textMain}`}>{t.checkerTitle}</h2>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold border ${isComplete ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'}`}>
                 {isComplete ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                 {isComplete ? t.analysisComplete : t.analysisIncomplete}
              </div>
           </div>
           
           <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              {result.isValidUI && (
                <button
                  onClick={toggleLayout}
                  className={`flex-1 lg:flex-none justify-center items-center flex gap-2 px-5 py-2 border rounded-xl text-sm font-bold transition-all active:scale-95 ${
                    isDark
                      ? 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-white'
                      : 'bg-white hover:bg-slate-100 border-slate-300 hover:border-black text-slate-700 hover:text-slate-900 shadow-sm hover:shadow-slate-300/50'
                  }`}
                  title={isFullWidthLayout ? t.checkerSplitView : t.checkerFullView}
                  aria-label={isFullWidthLayout ? t.checkerSwitchToSplitView : t.checkerSwitchToFullView}
                >
                  {isFullWidthLayout ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
                  {isFullWidthLayout ? t.checkerSplitView : t.checkerFullView}
                </button>
              )}
              <button 
                onClick={handleReset}
                className={`flex-1 lg:flex-none justify-center items-center flex gap-2 px-5 py-2 border rounded-xl text-sm font-bold transition-all active:scale-95 ${isDark ? 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-white' : 'bg-white hover:bg-slate-100 border-slate-300 hover:border-black text-slate-700 hover:text-slate-900 shadow-sm hover:shadow-slate-300/50'}`}
              >
                <RotateCcw className="w-4 h-4" /> {t.uploadAnother}
              </button>
              
              <button 
                onClick={handleExportPDF}
                className="flex-1 lg:flex-none justify-center items-center flex gap-2 px-5 py-2 border border-transparent hover:border-white/20 rounded-xl text-sm font-normal text-white transition-all active:scale-95 bg-[#038759] hover:bg-[#026e49] shadow-lg shadow-emerald-900/30"
              >
                <Download className="w-4 h-4" /> {t.checkerDownloadPdf}
              </button>
           </div>
        </div>

        {!result.isValidUI ? (
           <div className={`border rounded-xl p-10 text-center ${isDark ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-200'}`} role="alert">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className={`text-xl font-bold mb-2 ${textMain}`}>{t.invalidUI}</h3>
              <p className={`mb-6 max-w-lg mx-auto ${textSub}`}>{t.invalidUIMsg}</p>
              <button onClick={handleReset} className="text-red-500 hover:text-red-600 font-bold underline rounded px-2">{t.tryAgain}</button>
           </div>
        ) : (
          <div className={`grid gap-8 ${isFullWidthLayout ? 'grid-cols-1' : 'lg:grid-cols-12'}`}>
             
             {/* LEFT/TOP: Image with Interactive Overlay */}
             <div className={`${isFullWidthLayout ? 'w-full' : 'lg:col-span-4 lg:max-w-[520px]'} space-y-6`}>
                <div className={`${glassPanelClass} p-0 sticky top-32 overflow-hidden`}>

                   {/* Hidden Screen Reader List for Navigation */}
                   {result.complexSections && result.complexSections.length > 0 && (
                       <div className="sr-only">
                           <h3>{t.sectionsComponents}</h3>
                           <ol>
                               {result.complexSections.map((section, idx) => (
                                   <li key={idx}>
                                       <button 
                                           onClick={() => handleZoneClick(idx)}
                                           aria-pressed={selectedSectionIndex === idx}
                                       >
                                           {section.regionTitle}
                                       </button>
                                   </li>
                               ))}
                           </ol>
                       </div>
                   )}
                   
                   <div className="relative w-full group select-none">
                      <img 
                        src={`data:image/png;base64,${image}`} 
                        alt="Analyzed UI" 
                        className="w-full h-auto block pointer-events-none"
                      />
                      
                      {/* Overlay Container: pointer-events-none to let clicks pass to image/bg if not on button, but buttons have pointer-events-auto */}
                      <div className="absolute inset-0 z-10 pointer-events-none" aria-hidden="true">
                        {result.complexSections?.map((section, idx) => {
                            const overlayRect = resolveOverlayRect(section.box_2d, overlayContext);
                            if (!overlayRect) return null;
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleZoneClick(idx);
                                    }}
                                    onMouseEnter={() => setHoveredSectionIndex(idx)}
                                    onMouseLeave={() => setHoveredSectionIndex(null)}
                                    onFocus={() => setHoveredSectionIndex(idx)}
                                    onBlur={() => setHoveredSectionIndex(null)}
                                    tabIndex={0}
                                    className={`absolute cursor-pointer transition-all duration-200 outline-none pointer-events-auto
                                        ${selectedSectionIndex === idx
                                            ? 'border-2 border-blue-400 bg-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.3)] z-30'
                                            : hoveredSectionIndex === idx
                                                ? `border-2 z-20 ${isDark ? 'border-white/70 bg-white/15' : 'border-blue-500/60 bg-blue-500/12'}`
                                                : `${isDark ? 'border border-emerald-300/45 bg-emerald-400/10' : 'border border-emerald-600/45 bg-emerald-500/10'} z-10`
                                        }
                                    `}
                                    style={{
                                        top: `${overlayRect.topPct}%`,
                                        left: `${overlayRect.leftPct}%`,
                                        width: `${overlayRect.widthPct}%`,
                                        height: `${overlayRect.heightPct}%`,
                                        minWidth: '24px',
                                        minHeight: '24px',
                                    }}
                                >
                                    {(hoveredSectionIndex === idx || selectedSectionIndex === idx) && (
                                        <div className="absolute -top-8 left-0 bg-black/90 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap border border-white/20 z-40 pointer-events-none">
                                            {section.regionTitle}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                      </div>
                      
                      <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md text-[10px] text-white/80 px-2 py-1 rounded-md pointer-events-none flex items-center gap-1 z-20" aria-hidden="true">
                         <MousePointerClick className="w-3 h-3" /> Click regions to filter
                      </div>
                   </div>

                   {userContext && (
                    <div className={`${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'} border-t p-4`}>
                        <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase mb-2">
                        <MessageSquareText className="w-4 h-4" aria-hidden="true" />
                        <span>{t.contextOptional}</span>
                        </div>
                        <p className={`text-sm italic ${textSub}`}>"{userContext}"</p>
                    </div>
                   )}
                </div>
             </div>

             {/* RIGHT/BOTTOM: Hierarchical Audit Panel */}
             <div className={`${isFullWidthLayout ? 'w-full' : 'lg:col-span-8'} space-y-6`}>
                
                {selectedSectionIndex === null && (
                    <DetectedScreenAccordion 
                      t={t} 
                      screenContext={result.screenContext} 
                      imageResolution={imageResolution} 
                      renderContextValue={renderContextValue} 
                      theme={theme}
                    />
                )}

                {selectedSectionIndex === null && (
                    <div className="space-y-4">
                        <h3 className="text-xs font-bold uppercase text-slate-500 tracking-widest pl-2">{t.checkerPdfGlobalAccessibility}</h3>
                        
                        <GlobalAccordion 
                            title={t.checkerPdfViewTitle} 
                            content={result.globalAnnotations?.viewTitle} 
                            icon={<AppWindow className="w-5 h-5 text-pink-500" aria-hidden="true" />} 
                            theme={theme}
                        />
                        <GlobalAccordion 
                            title={t.checkerPdfStructure} 
                            content={result.globalAnnotations?.structure} 
                            icon={<Layers className="w-5 h-5 text-teal-500" aria-hidden="true" />} 
                            theme={theme}
                        />
                        <GlobalAccordion 
                            title={t.checkerPdfHeading} 
                            content={result.globalAnnotations?.heading} 
                            icon={<Type className="w-5 h-5 text-indigo-500" aria-hidden="true" />} 
                            theme={theme}
                        />
                        <GlobalAccordion 
                            title={t.checkerPdfFocusOrder} 
                            content={result.globalAnnotations?.focusOrder} 
                            icon={<ListOrdered className="w-5 h-5 text-yellow-500" aria-hidden="true" />} 
                            theme={theme}
                        />
                    </div>
                )}

                <div className="space-y-4">
                   <div className="flex justify-between items-center pl-2 mt-8 mb-2">
                        <h3 className="text-xs font-bold uppercase text-slate-500 tracking-widest">
                            {selectedSectionIndex !== null 
                                ? result.complexSections[selectedSectionIndex].regionTitle 
                                : (t.sectionsComponents || "Sections/Components")
                            }
                        </h3>
                        {selectedSectionIndex !== null && (
                            <button 
                                onClick={() => setSelectedSectionIndex(null)}
                                className="text-xs text-blue-500 hover:text-blue-600 transition-colors flex items-center gap-1 font-bold focus:outline-none focus:underline"
                            >
                                <ArrowLeft className="w-3 h-3" /> {t.checkerSeeAll}
                            </button>
                        )}
                   </div>
                   
                   {result.complexSections?.map((section, idx) => {
                     if (selectedSectionIndex !== null && selectedSectionIndex !== idx) return null;

                     const componentDocs = section.components?.length
                        ? section.components
                        : [
                            {
                              componentTitle: section.regionTitle,
                              categories: section.categories,
                            },
                          ];

                     return (
                        <SectionAccordion 
                            key={idx} 
                            title={`${t.checkerSectionPrefix} - ${section.regionTitle}`} 
                            isSelected={selectedSectionIndex === idx}
                            theme={theme}
                        >
                            <div className="py-1">
                            {componentDocs.map((component, componentIdx) => (
                              <ComponentAccordion
                                key={`${idx}-${componentIdx}-${component.componentTitle}`}
                                title={`${t.checkerComponentPrefix} - ${component.componentTitle}`}
                                theme={theme}
                              >
                                <CategoryBlock title={t.specAccess} content={component.categories.generalSpecific} theme={theme} />
                                <CategoryBlock title={t.behavior} content={component.categories.behavior} theme={theme} />
                                <CategoryBlock title="ALT" content={component.categories.alt} theme={theme} />
                                <CategoryBlock title={t.checkerPdfGrouped} content={component.categories.grouped} theme={theme} />
                              </ComponentAccordion>
                            ))}
                            </div>
                        </SectionAccordion>
                     );
                   })}

                   {result.complexSections?.length === 0 && (
                       <p className={`italic text-sm pl-2 ${textSub}`}>{t.checkerNoSectionsDetected}</p>
                   )}
                </div>

             </div>
          </div>
        )}
      </div>
    );
  };
}
